import { Conversations451Api } from '../conversations451';
import { FeatureFlagsApi } from '../feature-flags';
import { SegmentsApi } from '../segments';

export interface StudentAssistant {
  guid: string;
  name: string | null;
  logo: string | null;
  greeting: string | null;
  title: string | null;
  assistant_type: StudentAssistantType;
  assistant_tools: Conversations451Api.ChatbotTools[];
  assistant_sis_tool: Conversations451Api.ChatbotSisTools | null;
  description: string | null;
  internal_description?: string | null;
  config: StudentAssistantConfig | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  assistant_subtype?: StudentAssistantSubtype[] | null;
  voice?: string | null;
  user_data_fields?: string[] | null;
  voice_style?: string | null;
  inbox_id?: string | null;
  phone_id?: string | null;
}

export type DefaultStudentAssistant = Omit<
  StudentAssistant,
  'assistant_type' | 'assistant_tools' | 'assistant_sis_tool' | 'title'
> & {
  name: string;
  assistant_type: 'default';
};

export interface StudentAssistantConfig {
  limit?: {
    knowledge?: {
      guids?: string[];
      categories?: Conversations451Api.KnowledgeBaseCategories[];
      url_pattern?: string[];
      title_pattern?: string[];
    };
    events?: {
      guids?: string[];
      categories?: string[];
    };
    applications?: {
      guids?: string[];
    };
    appointments?: {
      guids?: string[];
      categories?: string[];
    };
  };
}

export enum StudentAssistantConfigResource {
  Knowledge = 'knowledge.guids',
  Applications = 'applications.guids',
  Events = 'events.guids',
  EventCategories = 'events.categories',
  Appointments = 'appointments.guids',
  AppointmentCategories = 'appointments.categories'
}

export type CreateStudentAssistantDto = Pick<
  StudentAssistant,
  | 'name'
  | 'title'
  | 'assistant_type'
  | 'assistant_tools'
  | 'assistant_sis_tool'
  | 'logo'
  | 'enabled'
  | 'description'
  | 'internal_description'
  | 'greeting'
  | 'config'
  | 'assistant_subtype'
  | 'voice'
  | 'user_data_fields'
  | 'voice_style'
  | 'inbox_id'
  | 'phone_id'
>;

export type UpdateStudentAssistantDto = Partial<
  Pick<
    StudentAssistant,
    | 'name'
    | 'title'
    | 'assistant_type'
    | 'assistant_tools'
    | 'assistant_sis_tool'
    | 'logo'
    | 'enabled'
    | 'description'
    | 'internal_description'
    | 'greeting'
    | 'config'
    | 'assistant_subtype'
    | 'voice'
    | 'user_data_fields'
    | 'voice_style'
    | 'inbox_id'
    | 'phone_id'
  >
>;

export enum StudentAssistantType {
  AcademicAdvisor = 'academic-advisor',
  CareerAdvisor = 'career-advisor',
  FinancialAidAdvisor = 'financial-aid-advisor',
  LeadGen = 'lead-gen',
  Recruiter = 'recruiter',
  CommonAppAdvisor = 'common-app-advisor',
  AdmissionAdvisor = 'admission-advisor',
  CampusLifeAdvisor = 'campus-life-advisor',
  PeerAdvisor = 'peer-advisor',
  AlumniAdvisor = 'alumni-advisor',
  AthleticsAdvisor = 'athletics-advisor',
  QAChatbot = 'qa-chatbot'
}

export const STUDENT_ASSISTANT_TYPE_LABELS: Record<
  StudentAssistantType | 'default',
  string
> = {
  [StudentAssistantType.AcademicAdvisor]: 'Academic Advisor',
  [StudentAssistantType.CareerAdvisor]: 'Career Advisor',
  [StudentAssistantType.FinancialAidAdvisor]: 'Financial Aid Advisor',
  [StudentAssistantType.LeadGen]: 'Lead Gen',
  [StudentAssistantType.Recruiter]: 'Recruiter',
  [StudentAssistantType.CommonAppAdvisor]: 'Common App Advisor',
  [StudentAssistantType.AdmissionAdvisor]: 'Admissions Advisor',
  [StudentAssistantType.CampusLifeAdvisor]: 'Campus Life Advisor',
  [StudentAssistantType.PeerAdvisor]: 'Peer Advisor',
  [StudentAssistantType.AlumniAdvisor]: 'Alumni Relations Officer',
  [StudentAssistantType.AthleticsAdvisor]: 'Athletics Counselor',
  [StudentAssistantType.QAChatbot]: 'QA Chatbot',
  default: 'Default'
};

export const STUDENT_ASSISTANT_TYPE_DEFAULT_AVATAR: Record<
  StudentAssistantType,
  string
> = {
  [StudentAssistantType.AcademicAdvisor]:
    'https://451.imgix.net/shared/bolt-agents/bolt-agent1.png',
  [StudentAssistantType.CareerAdvisor]:
    'https://451.imgix.net/shared/bolt-agents/bolt-agent2.png',
  [StudentAssistantType.FinancialAidAdvisor]:
    'https://451.imgix.net/shared/bolt-agents/bolt-agent3.png',
  [StudentAssistantType.CommonAppAdvisor]:
    'https://451.imgix.net/shared/bolt-agents/bolt-agent4.png',
  [StudentAssistantType.AdmissionAdvisor]:
    'https://451.imgix.net/shared/bolt-agents/bolt-agent5.png',
  [StudentAssistantType.CampusLifeAdvisor]:
    'https://451.imgix.net/shared/bolt-agents/bolt-agent6.png',
  [StudentAssistantType.PeerAdvisor]:
    'https://451.imgix.net/shared/bolt-agents/bolt-agent7.png',
  [StudentAssistantType.AlumniAdvisor]:
    'https://451.imgix.net/shared/bolt-agents/bolt-agent8.png',
  [StudentAssistantType.AthleticsAdvisor]:
    'https://451.imgix.net/shared/bolt-agents/bolt-agent9.png',
  [StudentAssistantType.LeadGen]:
    'https://451.imgix.net/shared/bolt-agents/bolt-agent5.png',
  [StudentAssistantType.Recruiter]:
    'https://451.imgix.net/shared/bolt-agents/bolt-agent5.png',
  [StudentAssistantType.QAChatbot]:
    'https://451.imgix.net/shared/bolt-agents/bolt-agent1.png'
};

export const STUDENT_ASSISTANT_TYPE_FEATURE_FLAGS: Record<
  StudentAssistantType,
  FeatureFlagsApi.FeatureFlags
> = {
  [StudentAssistantType.AcademicAdvisor]:
    FeatureFlagsApi.FeatureFlags.AcademicAdvisorAgent,
  [StudentAssistantType.CareerAdvisor]:
    FeatureFlagsApi.FeatureFlags.CareerAdvisorAgent,
  [StudentAssistantType.FinancialAidAdvisor]:
    FeatureFlagsApi.FeatureFlags.FinancialAidAdvisorAgent,
  [StudentAssistantType.LeadGen]: FeatureFlagsApi.FeatureFlags.LeadGenAgent,
  [StudentAssistantType.Recruiter]: FeatureFlagsApi.FeatureFlags.RecruiterAgent,
  [StudentAssistantType.CommonAppAdvisor]:
    FeatureFlagsApi.FeatureFlags.CommonAppAdvisorAgent,
  [StudentAssistantType.AdmissionAdvisor]:
    FeatureFlagsApi.FeatureFlags.AdmissionAdvisorAgent,
  [StudentAssistantType.CampusLifeAdvisor]:
    FeatureFlagsApi.FeatureFlags.CampusLifeAdvisorAgent,
  [StudentAssistantType.PeerAdvisor]:
    FeatureFlagsApi.FeatureFlags.PeerAdvisorAgent,
  [StudentAssistantType.AlumniAdvisor]:
    FeatureFlagsApi.FeatureFlags.AlumniAdvisorAgent,
  [StudentAssistantType.AthleticsAdvisor]:
    FeatureFlagsApi.FeatureFlags.AthleticsAdvisorAgent,
  [StudentAssistantType.QAChatbot]: FeatureFlagsApi.FeatureFlags.QAChatbotAgent
};

export const STUDENT_ASSISTANT_TYPE_DESCRIPTIONS: Record<
  StudentAssistantType | 'default',
  string
> = {
  [StudentAssistantType.AcademicAdvisor]:
    'Supports current students throughout their academic journey, detecting when they may need help and guiding them through key processes like course registration.',
  [StudentAssistantType.CareerAdvisor]:
    'Drives inquiries, boosts application starts, and fills recruiting events',
  [StudentAssistantType.FinancialAidAdvisor]:
    'Helps prospective and current students understand financial aid and tuition at the university, surfacing key deadlines and providing support with any money-related questions',
  [StudentAssistantType.LeadGen]:
    'Prioritizes top-of-funnel goals: converting anonymous website visitors or inquiries into qualified leads',
  [StudentAssistantType.Recruiter]:
    'Focuses on converting inquiries or prospects to applicants. Operates in contexts like post-inquiry follow-up, event registration, or outbound outreach.',
  [StudentAssistantType.CommonAppAdvisor]:
    'Monitors student progress, identifies risks, and connects them to support resources.',
  [StudentAssistantType.AdmissionAdvisor]:
    'Drives completed applications by engaging with students proactively and helping them through the process',
  [StudentAssistantType.CampusLifeAdvisor]:
    'Promotes student engagement and mental health by supporting students with key on-campus logistics like housing and scheduling, staying in close contact with students and inviting them to participate in campus life',
  [StudentAssistantType.PeerAdvisor]:
    'Supports students with academic and personal challenges, connecting them with resources and helping them navigate the university community',
  [StudentAssistantType.AlumniAdvisor]:
    'Helps students decide on their academic path based on their career plans, facilitating event attendance and staff engagement to support the student',
  [StudentAssistantType.AthleticsAdvisor]:
    'Supports student-athletes before and after enrollment by providing clear information on the school’s athletics programs and facilitating direct staff support.',
  [StudentAssistantType.QAChatbot]:
    'Provides a chatbot interface for students to ask questions and get answers from the university.',
  default: 'Default'
};

export const STUDENT_ASSISTANT_TYPE_DEFAULT_TOOLS: Record<
  StudentAssistantType,
  Conversations451Api.ChatbotTools[]
> = {
  [StudentAssistantType.AcademicAdvisor]: [
    Conversations451Api.ChatbotTools.ScheduleAppointment,
    Conversations451Api.ChatbotTools.ProgramExplorer,
    Conversations451Api.ChatbotTools.CourseCatalog,
    Conversations451Api.ChatbotTools.TeamMember
  ],
  [StudentAssistantType.CareerAdvisor]: [
    Conversations451Api.ChatbotTools.TeamMember,
    Conversations451Api.ChatbotTools.ProgramExplorer,
    Conversations451Api.ChatbotTools.CourseCatalog
  ],
  [StudentAssistantType.FinancialAidAdvisor]: [
    Conversations451Api.ChatbotTools.TeamMember,
    Conversations451Api.ChatbotTools.FinancialAidKnowledgeBase,
    Conversations451Api.ChatbotTools.ScheduleAppointment
  ],
  [StudentAssistantType.LeadGen]: [
    Conversations451Api.ChatbotTools.TeamMember,
    Conversations451Api.ChatbotTools.InquiryFlow
  ],
  [StudentAssistantType.Recruiter]: [
    Conversations451Api.ChatbotTools.ScheduleAppointment,
    Conversations451Api.ChatbotTools.TeamMember,
    Conversations451Api.ChatbotTools.RegisterForEvent,
    Conversations451Api.ChatbotTools.StartApplication
  ],
  [StudentAssistantType.CommonAppAdvisor]: [
    Conversations451Api.ChatbotTools.TeamMember,
    Conversations451Api.ChatbotTools.CommonAppKnowledgeBase
  ],
  [StudentAssistantType.AdmissionAdvisor]: [
    Conversations451Api.ChatbotTools.ApplicationStatusChecklist,
    Conversations451Api.ChatbotTools.StartApplication,
    Conversations451Api.ChatbotTools.ScheduleAppointment,
    Conversations451Api.ChatbotTools.RegisterForEvent,
    Conversations451Api.ChatbotTools.GetApplicationStatus,
    Conversations451Api.ChatbotTools.TeamMember
  ],
  [StudentAssistantType.CampusLifeAdvisor]: [
    Conversations451Api.ChatbotTools.ScheduleAppointment,
    Conversations451Api.ChatbotTools.RegisterForEvent,
    Conversations451Api.ChatbotTools.TeamMember
  ],
  [StudentAssistantType.PeerAdvisor]: [
    Conversations451Api.ChatbotTools.RegisterForEvent,
    Conversations451Api.ChatbotTools.TeamMember
  ],
  [StudentAssistantType.AlumniAdvisor]: [
    Conversations451Api.ChatbotTools.ScheduleAppointment,
    Conversations451Api.ChatbotTools.TeamMember
  ],
  [StudentAssistantType.AthleticsAdvisor]: [
    Conversations451Api.ChatbotTools.ScheduleAppointment,
    Conversations451Api.ChatbotTools.RegisterForEvent,
    Conversations451Api.ChatbotTools.TeamMember
  ],
  [StudentAssistantType.QAChatbot]: [
    Conversations451Api.ChatbotTools.TeamMember,
    Conversations451Api.ChatbotTools.InquiryFlow
  ]
};

export type StudentAssistantSubtype = AdmissionsAssistantSubtype;

export enum AdmissionsAssistantSubtype {
  Undergraduate = 'undegraduate',
  Graduate = 'graduate',
  ContinuingEducation = 'continuing-education',
  International = 'international',
  Domestic = 'domestic'
}

export const ADMISSIONS_ASSISTANT_SUBTYPE_LABELS: Record<
  AdmissionsAssistantSubtype,
  string
> = {
  [AdmissionsAssistantSubtype.Undergraduate]: 'Undergraduate',
  [AdmissionsAssistantSubtype.Graduate]: 'Graduate',
  [AdmissionsAssistantSubtype.ContinuingEducation]: 'Continuing Education',
  [AdmissionsAssistantSubtype.International]: 'International',
  [AdmissionsAssistantSubtype.Domestic]: 'Domestic'
};

export interface StudentAssistantsGroup {
  guid: string;
  name: string;
  enabled: boolean;
  assistants: string[];
  conditions: SegmentsApi.SegmentValue3 | null;
  index_weight: number;
  created_at: string;
  updated_at: string;
}

export type CreateStudentAssistantsGroupDto = Pick<
  StudentAssistantsGroup,
  'name' | 'assistants' | 'conditions' | 'enabled'
>;

export type UpdateStudentAssistantsGroupDto = Partial<
  Pick<StudentAssistantsGroup, 'name' | 'assistants' | 'conditions' | 'enabled'>
>;

export const getStudentAssistantName = (
  assistant:
    | Pick<StudentAssistant, 'name' | 'assistant_type'>
    | undefined
    | null,
  prefix?: string
) => {
  if (!assistant) return 'N/A';

  const label = STUDENT_ASSISTANT_TYPE_LABELS[assistant.assistant_type];
  const type = prefix ? `${prefix} ${label}` : label;

  return assistant.name || type || 'N/A';
};

export const formatAssistantHandoffToolName = (
  assistant:
    | Pick<StudentAssistant, 'name' | 'assistant_type' | 'assistant_subtype'>
    | undefined
    | null
) => {
  const assistantName = assistant?.name
    ? assistant.name
        .toLowerCase()
        .replace(/ /g, '-')
        .replace(/[^a-zA-Z0-9_-]/g, '')
    : '';

  const handoffSuffix = 'handoff';

  let toolName = [assistant?.assistant_type, assistantName, handoffSuffix]
    .filter(Boolean)
    .join('-');

  if (toolName.length <= 64) {
    return toolName;
  }

  // if name is too long we will use only assistant type
  toolName = [assistant?.assistant_type, handoffSuffix].join('-');
  return toolName;
};

export enum AvatarType {
  Cartoonish = 'cartoonish',
  Realistic = 'realistic'
}

export interface GenerateAvatarDto {
  type: AvatarType;
  instructions?: string;
  appearance: string;
  assistant_type?: StudentAssistantType;
}

export const createDefaultStudentAssistant = (
  chatbotSettings: Conversations451Api.ConversationsChatbotSettingsDto
): DefaultStudentAssistant => ({
  guid: 'default',
  assistant_type: 'default',
  name: chatbotSettings.chatbot_name || '',
  logo: chatbotSettings.chatbot_avatar,
  greeting: chatbotSettings.chatbot_greeting,
  voice: chatbotSettings.chatbot_voice,
  voice_style: chatbotSettings.chatbot_voice_style,
  description: null,
  internal_description: null,
  config: null,
  enabled: true,
  created_at: '',
  updated_at: '',
  user_data_fields: null
});

export const isDefaultStudentAssistant = (
  assistant:
    | Omit<StudentAssistant, 'created_at' | 'updated_at'>
    | DefaultStudentAssistant
    | { guid: string }
): assistant is DefaultStudentAssistant => assistant.guid === 'default';
